import React from 'react';
import { Collapse } from 'antd';
import moment from 'moment';

const { Panel } = Collapse;

const LABEL_KATEGORI = {
	identitas_pemilik: 'Identitas Pemilik',
	identitas_kendaraan: 'Identitas Kendaraan',
	detail_nominal: 'Detail Nominal',
};

const LABEL_SUB_NOMINAL = {
	bbnkb: 'BBNKB', opsen_bbnkb: 'Opsen BBNKB', pkb: 'PKB', opsen_pkb: 'Opsen PKB',
	swdkllj: 'SWDKLLJ', sp3: 'SP3', pnbp: 'PNBP', status_transaksi: 'Status Transaksi',
};

// Urutan tampilan EKSPLISIT, TIDAK ikut urutan apa adanya dari data - kolom
// `detail` tersimpan sbg PostgreSQL `jsonb`, yang TIDAK menjamin urutan key
// asli dipertahankan (diurutkan ulang oleh Postgres sendiri, bukan bug).
// Daftar di bawah ini SATU-SATUNYA sumber urutan tampilan, harus disesuaikan
// manual kalau field baru ditambah di backend. Lihat diskusi rencana 2026-10-17.
const URUTAN_IDENTITAS_PEMILIK = ['no_hp', 'nik_pemilik', 'nama_pemilik', 'alamat_pemilik'];

const URUTAN_IDENTITAS_KENDARAAN = [
	'nomor_polisi', 'jenis', 'merek', 'tipe', 'no_rangka', 'no_mesin', 'no_bpkb',
	'cc', 'bbm', 'jumlah_roda', 'warna_kendaraan', 'warna_plat',
	'tgl_stnk', 'sd_stnk', 'tgl_pkb', 'sd_pkb', 'tgl_swdkllj', 'sd_swdkllj',
	'tahun_buat', 'njkb', 'golongan',
];

const URUTAN_NOMINAL_KATEGORI = ['bbnkb', 'opsen_bbnkb', 'pkb', 'opsen_pkb', 'swdkllj', 'sp3', 'pnbp', 'status_transaksi'];

const URUTAN_NOMINAL_SUB = {
	bbnkb: ['pokok', 'denda', 'tunggakan_pokok', 'tunggakan_denda', 'diskon_pokok', 'diskon_denda'],
	opsen_bbnkb: ['pokok', 'denda', 'tunggakan_pokok', 'tunggakan_denda', 'diskon_pokok', 'diskon_denda'],
	pkb: ['pokok', 'denda', 'tunggakan_pokok', 'tunggakan_denda', 'diskon_pokok', 'diskon_denda', 'diskon_tunggakan_pokok', 'diskon_tunggakan_denda'],
	opsen_pkb: ['pokok', 'denda', 'tunggakan_pokok', 'tunggakan_denda', 'diskon_pokok', 'diskon_denda', 'diskon_tunggakan_pokok', 'diskon_tunggakan_denda'],
	swdkllj: ['pokok', 'denda', 'tunggakan_pokok', 'tunggakan_denda', 'diskon_tunggakan_pokok', 'diskon_tunggakan_denda', 'diskon_pokok', 'diskon_denda'],
	sp3: ['pokok', 'denda', 'jumlah'],
	pnbp: ['tnkb', 'stnk'],
	status_transaksi: ['status', 'is_edit', 'is_online', 'is_bbnkb_i', 'payment_type', 'is_print_stnk', 'is_notice_failed', 'is_print_kutipan'],
};

const POLA_TANGGAL_ISO = /^\d{4}-\d{2}-\d{2}$/;

function formatValue(v) {
	if (v === null || v === undefined || v === '') return '—';
	if (typeof v === 'boolean') return v ? 'Ya' : 'Tidak';
	if (typeof v === 'number') return v.toLocaleString('id-ID');
	if (typeof v === 'string' && POLA_TANGGAL_ISO.test(v)) return moment(v).format('DD-MM-YYYY');
	return String(v);
}

// Susun ulang entries sesuai `urutan` (daftar key eksplisit) - key yang ADA
// di data tapi BELUM terdaftar di `urutan` tetap ditampilkan (di akhir, apa
// adanya), supaya field baru yang lupa didaftarkan tidak diam-diam hilang.
function urutkanEntries(obj, urutan) {
	const sisa = new Map(Object.entries(obj || {}));
	const hasil = [];
	(urutan || []).forEach((key) => {
		if (sisa.has(key)) { hasil.push([key, sisa.get(key)]); sisa.delete(key); }
	});
	sisa.forEach((value, key) => hasil.push([key, value]));
	return hasil;
}

function KeyValueRows({ obj, urutan }) {
	const entries = urutkanEntries(obj, urutan);
	if (entries.length === 0) {
		return <div style={{ padding: 10, color: '#aaa', fontSize: 12 }}>Tidak ada data</div>;
	}
	return (
		<div>
			{entries.map(([key, value], i) => (
				<div key={key} style={{
					display: 'flex', gap: 12, padding: '7px 10px',
					background: i % 2 === 0 ? '#f9fbff' : '#fff',
					borderRadius: 6, marginBottom: 2,
				}}>
					<div style={{ flexShrink: 0, width: 150, fontSize: 11, fontWeight: 600, color: '#1e4d8a', fontFamily: 'monospace' }}>{key}</div>
					<div style={{ flex: 1, fontSize: 12, color: '#222', wordBreak: 'break-word' }}>{formatValue(value)}</div>
				</div>
			))}
		</div>
	);
}

// Tampilan terkategori per Collapse (detail_nominal/identitas_pemilik/
// identitas_kendaraan) - menggantikan dump JSON mentah. `detail` = field
// `detail` JSONB dari UserActivityLog apa adanya. Key lain di luar 3
// kategori ini (mis. jumlah_hasil, mutasi_terdeteksi, pesan) ditampilkan
// polos di atas Collapse. Urutan panel SENGAJA detail_nominal dulu (kalau
// ada - cuma muncul utk tetapkan/hapus_penetapan), baru identitas_pemilik,
// baru identitas_kendaraan - urutan ini otomatis cocok juga utk event yang
// tidak punya detail_nominal (cek_nopol_*/daftar), krn panel itu cuma
// skip kalau datanya tidak ada. Lihat diskusi rencana 2026-10-17.
export default function DetailAktivitasCollapse({ detail }) {
	if (!detail || Object.keys(detail).length === 0) {
		return <div style={{ padding: 16, color: '#aaa', fontSize: 12 }}>Tidak ada detail</div>;
	}
	const { identitas_pemilik, identitas_kendaraan, detail_nominal, ...lainnya } = detail;
	const adaLainnya = Object.keys(lainnya).length > 0;

	return (
		<div>
			{adaLainnya && <div style={{ marginBottom: 10 }}><KeyValueRows obj={lainnya} /></div>}
			{(identitas_pemilik || identitas_kendaraan || detail_nominal) && (
				<Collapse defaultActiveKey={[]} bordered={false} style={{ background: '#fff' }}>
					{identitas_pemilik && (
						<Panel header={LABEL_KATEGORI.identitas_pemilik} key="identitas_pemilik">
							<KeyValueRows obj={identitas_pemilik} urutan={URUTAN_IDENTITAS_PEMILIK} />
						</Panel>
					)}
					{identitas_kendaraan && (
						<Panel header={LABEL_KATEGORI.identitas_kendaraan} key="identitas_kendaraan">
							<KeyValueRows obj={identitas_kendaraan} urutan={URUTAN_IDENTITAS_KENDARAAN} />
						</Panel>
					)}
					{detail_nominal && (
						<Panel header={LABEL_KATEGORI.detail_nominal} key="detail_nominal">
							{urutkanEntries(detail_nominal, URUTAN_NOMINAL_KATEGORI).map(([sub, obj]) => (
								<div key={sub} style={{ marginBottom: 14 }}>
									<div style={{ fontSize: 11, fontWeight: 700, color: '#0f2847', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
										{LABEL_SUB_NOMINAL[sub] || sub}
									</div>
									<KeyValueRows obj={obj} urutan={URUTAN_NOMINAL_SUB[sub]} />
								</div>
							))}
						</Panel>
					)}
				</Collapse>
			)}
		</div>
	);
}
